import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import OrderDetailsModal from "../../components/orderDetailsModal";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";
import {
  MONO_FONT,
  PAGE_FONT,
  SectionHead,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import { formatCount } from "../../components/metrics/metricsFormat";
import { fieldSx } from "../manage-catalog/constants";
import { isMobile } from "../../globalStyles/mobile";
import { supplyChainCostAnalysisService } from "../../utils/services/supplyChainCostAnalysisService";
import {
  clearSelection,
  retainAvailableSelection,
} from "../../utils/channelSelection";
import { orderService } from "../../utils/services/orderService";
import {
  normalizeWarehouseSelection,
  selectedWarehouseIds,
  warehouseIdsKey,
} from "../../utils/analyticsWarehouses";

const FIELD_HEIGHT = 36;
const fieldControlSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_HEIGHT,
    fontFamily: PAGE_FONT,
    fontSize: 13,
    borderRadius: "8px",
  },
};

const toolbarButtonSx = {
  height: FIELD_HEIGHT,
  fontSize: 13,
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatPct = (value) =>
  value == null || Number.isNaN(Number(value)) ? "—" : `${Number(value).toFixed(2)}%`;

// Fixed window, resolved by the server in the same timezone it buckets
// orders by: the last 7 complete calendar days, today excluded.
const ANALYSIS_PERIOD = "last_7_complete_days";

const formatDateShort = (dayKey) => {
  if (!dayKey) return "—";
  const d = new Date(`${dayKey}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", timeZone: "UTC" });
};

// Distinct, theme-agnostic swatches for the cost-breakdown stacked bar/table.
const BREAKDOWN_COLORS = ["#2E5FE8", "#22C55E", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"];

const FieldLabel = ({ children, colors }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      color: colors.textTertiary,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      mb: 0.5,
      lineHeight: 1,
    }}
  >
    {children}
  </Typography>
);

const ToolbarField = ({ label, colors, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <FieldLabel colors={colors}>{label}</FieldLabel>
    {children}
  </Box>
);

const ChannelMultiSelect = ({ options, selected, onChange, disabled, ui }) => {
  const { colors } = ui;
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options;
  }, [options, search]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const toggle = (value) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  const summaryLabel =
    selected.length === 0
      ? "All Channels"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} channels`;

  return (
    <>
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
        sx={{
          ...toolbarButtonSx,
          justifyContent: "space-between",
          minWidth: 170,
          maxWidth: 220,
          px: 1.5,
          backgroundColor: colors.bgPaper,
          borderColor: ui.borderStrong,
          color: selected.length ? colors.textPrimary : colors.textSecondary,
          "&:hover": { borderColor: ui.borderStrong, backgroundColor: colors.bgPaper },
        }}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {summaryLabel}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 270, maxHeight: 400, mt: 0.5 } }}
      >
        <Box sx={{ px: 1.25, pt: 0.75, pb: 0.5 }}>
          <TextField
            size="small"
            placeholder="Search channels…"
            fullWidth
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 15, color: colors.textTertiary, mr: 0.5 }} />
              ),
            }}
            sx={fieldControlSx}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.25, py: 0.25 }}>
          <Button
            size="small"
            disabled={options.length === 0}
            onClick={() => onChange([...options])}
            sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            Select All
          </Button>
          <Button
            size="small"
            disabled={selected.length === 0}
            onClick={() => onChange([])}
            sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            Clear All
          </Button>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
          {filteredOptions.length === 0 ? (
            <Typography sx={{ px: 2, py: 1.5, fontSize: 12.5, color: colors.textTertiary }}>
              No channels match &quot;{search}&quot;
            </Typography>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem
                key={option}
                onClick={() => toggle(option)}
                dense
                sx={{ fontSize: 13, py: 0.4 }}
              >
                <Checkbox size="small" checked={selected.includes(option)} sx={{ p: 0.5, mr: 0.5 }} />
                <ListItemText primary={option} primaryTypographyProps={{ fontSize: 13 }} />
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
};

const KpiCard = ({ label, value, sub, tooltip, ui }) => (
  <Box
    sx={{
      ...ui.card,
      padding: "14px 16px 13px",
      minHeight: 104,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: ui.colors.textSecondary,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: ui.colors.textTertiary }} />
        </Tooltip>
      )}
    </Box>
    <Typography
      sx={{
        fontSize: 24,
        fontWeight: 650,
        letterSpacing: "-0.01em",
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    <Box sx={{ mt: "auto", pt: 0.9, minHeight: 16 }}>
      {sub != null && (
        <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}>{sub}</Typography>
      )}
    </Box>
  </Box>
);

const COLUMN_DEFS = [
  { id: "order_id", label: "Order #", sort: "order_id", align: "left" },
  { id: "order_day", label: "Date", sort: "order_date", align: "left" },
  { id: "source", label: "Channel", align: "left" },
  { id: "warehouse_name", label: "Warehouse", align: "left" },
  { id: "units", label: "Units", align: "right" },
  { id: "total_bill", label: "Total Bill", sort: "total_bill", align: "right", money: true },
  { id: "product_cost", label: "Product", align: "right", money: true },
  {
    id: "future_inbound_cost",
    label: "Inbound",
    align: "right",
    placeholder: true,
    tooltip:
      "Inbound/import cost data (tariffs, duties, customs, and related costs) is not currently available.",
  },
  { id: "inbound_cost", label: "Receiving", align: "right", money: true },
  { id: "storage_cost", label: "Storage", align: "right", money: true },
  { id: "outbound_cost", label: "Fulfillment", align: "right", money: true },
  { id: "shipping_cost", label: "Shipping", align: "right", money: true },
  { id: "supply_chain_cost", label: "SCC", sort: "supply_chain_cost", align: "right", money: true },
  {
    id: "supply_chain_cost_pct",
    label: "SCC %",
    sort: "supply_chain_cost_pct",
    align: "right",
    pct: true,
  },
];

const SupplyChainCostAnalysisPage = () => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId;

  const [channelOptions, setChannelOptions] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  // [] = All warehouses (no filter — every warehouse of the client); else the
  // selected set, matched server-side with one `$in`.
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const warehouseKey = warehouseIdsKey(selectedWarehouseIds(selectedWarehouses));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState("supply_chain_cost_pct");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [clientId, selectedChannels, warehouseKey]);

  useEffect(() => {
    // Warehouses belong to a client — never carry a selection across clients.
    setSelectedWarehouses([]);
    if (!clientId) {
      setChannelOptions([]);
      setSelectedChannels(clearSelection);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await orderService.fetchOrderSource("", clientId, 50, 0);
        if (cancelled) return;
        const options = (data?.data || [])
          .map((row) => row?._id)
          .filter((value) => value != null && value !== "");
        setChannelOptions(options);
        // Same array when nothing changed, so the report is not fetched twice.
        setSelectedChannels((prev) => retainAvailableSelection(prev, options));
      } catch {
        if (!cancelled) {
          setChannelOptions([]);
          setSelectedChannels(clearSelection);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setPayload(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await supplyChainCostAnalysisService.fetch({
          clientId,
          period: ANALYSIS_PERIOD,
          channels: selectedChannels,
          warehouseIds: warehouseKey ? warehouseKey.split(",") : [],
          skip: page * rowsPerPage,
          limit: rowsPerPage,
          sortField,
          sortDir,
        });
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load supply chain cost analysis",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    selectedChannels,
    warehouseKey,
    reloadNonce,
    page,
    rowsPerPage,
    sortField,
    sortDir,
  ]);

  const pageLoading = Boolean(clientId) && loading;

  const toggleSort = (field) => {
    if (!field) return;
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const cellValue = (row, col) => {
    if (col.placeholder) return "—";
    const value = row[col.id];
    if (col.money) return formatMoney(value);
    if (col.pct) return formatPct(value);
    if (col.id === "units") return formatCount(value);
    return value || "—";
  };

  return (
    <Box
      sx={{
        backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="SC"
          title="Supply Chain Cost Analysis"
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.25 }}>
              {!storedClientId && (
                <ToolbarField label="Client" colors={colors}>
                  <ClientAutocomplete
                    width={200}
                    selectedClient={selectedClientData}
                    onClientChange={(client) => setSelectedClientData(client)}
                    hideLabel
                  />
                </ToolbarField>
              )}
              {channelOptions.length >= 2 && (
                <ToolbarField label="Channel" colors={colors}>
                  <ChannelMultiSelect
                    options={channelOptions}
                    selected={selectedChannels}
                    onChange={setSelectedChannels}
                    disabled={loading}
                    ui={ui}
                  />
                </ToolbarField>
              )}
              <ToolbarField label="Warehouse" colors={colors}>
                <AnalyticsWarehousePicker
                  clientId={clientId}
                  mode="multi"
                  value={selectedWarehouses}
                  onChange={(next) =>
                    setSelectedWarehouses(normalizeWarehouseSelection(next))
                  }
                  disabled={loading || !clientId}
                  width={220}
                  height={FIELD_HEIGHT}
                  triggerSx={{
                    ...toolbarButtonSx,
                    fontFamily: PAGE_FONT,
                    backgroundColor: colors.bgPaper,
                    borderColor: ui.borderStrong,
                    "&:hover": {
                      borderColor: ui.borderStrong,
                      backgroundColor: colors.bgPaper,
                    },
                  }}
                />
              </ToolbarField>
              <Button
                variant="outlined"
                onClick={() => setReloadNonce((n) => n + 1)}
                disabled={loading || !clientId}
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...toolbarButtonSx,
                  borderColor: ui.borderStrong,
                  color: colors.textSecondary,
                  backgroundColor: colors.bgPaper,
                }}
              >
                Refresh
              </Button>
            </Box>
          }
        />

        {!clientId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to load supply chain cost analysis.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}

        {pageLoading && (
          <SalesPerformancePageLoading
            title="Loading supply chain cost analysis..."
            kpiCount={4}
          />
        )}

        {!pageLoading && payload && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile()
                  ? "repeat(2, 1fr)"
                  : "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <KpiCard
                ui={ui}
                label="Analysis Period"
                value={`${formatDateShort(payload.start_date)} – ${formatDateShort(payload.end_date)}`}
                sub="Last 7 complete days · today excluded"
              />
              <KpiCard
                ui={ui}
                label="Orders Analyzed"
                value={formatCount(payload.totals.orders_analyzed)}
                sub={`${formatCount(payload.totals.orders_matched)} matched in range`}
                tooltip="Orders excluded for missing warehouse, rates, variant cost, shipping, quantity, or Total Bill are itemized below, under Excluded orders."
              />
              <KpiCard
                ui={ui}
                label="SCC % of Total Bill"
                value={formatPct(payload.totals.supply_chain_cost_pct_of_bill)}
                sub="Total SCC ÷ total bill, eligible orders"
              />
              <KpiCard
                ui={ui}
                label="Total Supply Chain Cost"
                value={formatMoney(payload.totals.total_supply_chain_cost)}
                sub="Eligible orders only"
              />
            </Box>

            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1.5, mb: 2 }}>
              <SectionHead
                title="Cost breakdown"
                subtitle="Product + inbound + storage + outbound + recorded shipping"
                center
              />
              {(payload.breakdown || []).length === 0 ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary, pb: 1 }}>
                  No eligible orders.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      height: 14,
                      borderRadius: "7px",
                      overflow: "hidden",
                      backgroundColor: ui.surfaceAlt,
                      mb: 2,
                    }}
                  >
                    {payload.breakdown.map((row, i) => {
                      const width = Math.max(0, Number(row.pct_of_scc) || 0);
                      if (width <= 0) return null;
                      return (
                        <Tooltip
                          key={row.component}
                          arrow
                          title={`${row.component}: ${formatMoney(row.amount)} · ${formatPct(row.pct_of_scc)}`}
                        >
                          <Box
                            sx={{
                              width: `${width}%`,
                              height: "100%",
                              backgroundColor: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
                              borderRight: `1px solid ${colors.bgPaper}`,
                              "&:last-of-type": { borderRight: "none" },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx(ui)}>Component</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Amount</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>% of SCC</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payload.breakdown.map((row, i) => (
                        <TableRow key={row.component}>
                          <TableCell sx={textCellSx(ui)}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "2px",
                                  flexShrink: 0,
                                  backgroundColor: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
                                }}
                              />
                              <Typography sx={{ fontSize: 13, textTransform: "capitalize" }}>
                                {row.component}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatMoney(row.amount)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatPct(row.pct_of_scc)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: isMobile() ? "1fr" : "1fr 1fr", gap: 2, mb: 2 }}>
              <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1 }}>
                <SectionHead title="By warehouse" subtitle="Eligible orders" center />
                {(payload.by_warehouse || []).length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: colors.textTertiary, pb: 2 }}>
                    No eligible orders.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx(ui)}>Warehouse</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Orders</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>SCC</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>SCC % of bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payload.by_warehouse.map((row) => (
                        <TableRow key={row.warehouse_id}>
                          <TableCell sx={textCellSx(ui)}>
                            {row.warehouse_name || row.warehouse_id}
                          </TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatCount(row.orders)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatMoney(row.supply_chain_cost)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>
                            {formatPct(row.supply_chain_cost_pct_of_bill)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
              <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1 }}>
                <SectionHead title="By channel" subtitle="Eligible orders" center />
                {(payload.by_channel || []).length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: colors.textTertiary, pb: 2 }}>
                    No eligible orders.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx(ui)}>Channel</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Orders</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>SCC</TableCell>
                        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>SCC % of bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payload.by_channel.map((row) => (
                        <TableRow key={row.channel}>
                          <TableCell sx={textCellSx(ui)}>{row.channel}</TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatCount(row.orders)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>{formatMoney(row.supply_chain_cost)}</TableCell>
                          <TableCell sx={numCellSx(ui)}>
                            {formatPct(row.supply_chain_cost_pct_of_bill)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Box>

            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1, mb: 2, overflowX: "auto" }}>
              <SectionHead
                title="High-cost orders"
                subtitle="Sorted by Supply Chain Cost % of Total Bill. Click a row for Order Details."
                center
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMN_DEFS.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={headCellSx(ui, {
                          textAlign: col.align === "right" ? "right" : "left",
                        })}
                      >
                        {col.sort ? (
                          <TableSortLabel
                            active={sortField === col.sort}
                            direction={sortField === col.sort ? sortDir : "desc"}
                            onClick={() => toggleSort(col.sort)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : col.tooltip ? (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            {col.label}
                            <Tooltip title={col.tooltip} arrow>
                              <InfoOutlinedIcon
                                sx={{ fontSize: 13, color: colors.textTertiary }}
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(payload.orders?.rows || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMN_DEFS.length} sx={textCellSx(ui)}>
                        No eligible orders in this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payload.orders.rows.map((row) => (
                      <TableRow
                        key={row._id || row.order_id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setSelectedOrder({ _id: row._id, order_id: row.order_id })}
                      >
                        {COLUMN_DEFS.map((col) => (
                          <TableCell
                            key={col.id}
                            sx={
                              col.align === "right"
                                ? numCellSx(ui, {
                                    fontWeight: col.id === "supply_chain_cost_pct" ? 700 : 400,
                                    color:
                                      col.id === "supply_chain_cost_pct" ? ui.danger : colors.textPrimary,
                                  })
                                : textCellSx(ui)
                            }
                          >
                            {cellValue(row, col)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={payload.orders?.total || 0}
                page={page}
                onPageChange={(_e, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Box>

            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 2, mb: 2 }}>
              <SectionHead
                title="Excluded orders"
                subtitle="These orders are omitted from averages. Missing required costs are not treated as zero."
                center
              />
              {(payload.exclusions || []).length === 0 ? (
                <Typography sx={{ fontSize: 13, color: colors.textTertiary }}>
                  No orders were excluded.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx(ui)}>Reason</TableCell>
                      <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Orders</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payload.exclusions.map((row) => (
                      <TableRow key={row.reason}>
                        <TableCell sx={textCellSx(ui)}>{row.label || row.reason}</TableCell>
                        <TableCell sx={numCellSx(ui)}>{formatCount(row.count)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textTertiary,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 1,
                }}
              >
                Calculation reference
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Supply Chain Cost
                </Box>
                {" — "}
                Product cost + inbound + storage + outbound + recorded shipping.
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Supply Chain Cost %
                </Box>
                {" — "}
                Supply Chain Cost ÷ Order Details Total Bill (total_price), per order. The bill may
                include tax and shipping.
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  SCC % of Total Bill
                </Box>
                {" — "}
                Σ Supply Chain Cost ÷ Σ Total Bill across eligible orders (headline and the
                warehouse / channel tables). Larger orders weigh more; it is not an average of
                per-order percentages.
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Analysis period
                </Box>
                {" — "}
                The last 7 complete calendar days; today&apos;s partial day is excluded.
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Product cost
                </Box>
                {" — "}
                Current catalog variant cost × quantity. Not a historical COGS snapshot.
              </Typography>
              <Typography
                sx={{ mt: 1.5, fontSize: 11, color: colors.textTertiary, fontFamily: MONO_FONT }}
              >
                {payload.meta?.shipping_cost_field} · {payload.meta?.warehouse_costs}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <OrderDetailsModal
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
      />
    </Box>
  );
};

export default SupplyChainCostAnalysisPage;
